import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Eye, Tag, Share2, Bookmark } from 'lucide-react';
import '../styles/ArticleDetails.css';

const ArticleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const API_BASE_URL = 'https://darkslateblue-eel-151368.hostingersite.com/api';

  // دالة لتصحيح مسارات الصور المكررة
  const getCorrectImagePath = (imagePath) => {
    if (!imagePath) return null;
    
    const baseUrl = 'https://darkslateblue-eel-151368.hostingersite.com';
    
    if (imagePath.startsWith('http')) {
      // إصلاح المسارات المكررة
      const corrected = imagePath.replace(
        /uploads\/articles\/uploads\/articles\//g,
        'uploads/articles/'
      );
      return corrected;
    }
    
    if (imagePath.startsWith('uploads/')) {
      return `${baseUrl}/storage/${imagePath}`;
    }
    
    if (imagePath.startsWith('storage/')) {
      return `${baseUrl}/${imagePath}`;
    }
    
    return `${baseUrl}/storage/${imagePath}`;
  };

  useEffect(() => {
    if (id) {
      fetchArticle();
    }
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // جلب المقال المحدد
      const response = await fetch(`${API_BASE_URL}/articles/${id}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'فشل في تحميل المقال');
      }
      
      const result = await response.json();
      
      // التعامل مع أنواع البيانات المختلفة
      let articleData;
      if (result.success && result.data) {
        articleData = result.data;
      } else if (result.data) {
        articleData = result.data;
      } else if (result) {
        articleData = result;
      } else {
        throw new Error('لم يتم العثور على المقال');
      }
      
      // تصحيح مسار الصورة
      articleData.image = getCorrectImagePath(articleData.image);
      
      setArticle(articleData);
      
      // جلب المقالات ذات الصلة من نفس التصنيف
      if (articleData.category) {
        fetchRelatedArticles(articleData.category);
      }
    } catch (err) {
      setError(err.message || 'حدث خطأ في تحميل المقال');
      console.error('Error fetching article:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async (category) => {
    try {
      const response = await fetch(`${API_BASE_URL}/articles?category=${encodeURIComponent(category)}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        let articlesData;
        if (result.success && result.data) {
          articlesData = result.data;
        } else if (result.data) {
          articlesData = result.data;
        } else if (Array.isArray(result)) {
          articlesData = result;
        } else {
          articlesData = [];
        }
        
        // تصفية المقال الحالي وتصحيح مسارات الصور
        const filtered = articlesData
          .filter(item => item.id !== parseInt(id))
          .map(item => ({
            ...item,
            image: getCorrectImagePath(item.image)
          }))
          .slice(0, 3);
        
        setRelatedArticles(filtered);
      }
    } catch (err) {
      console.error('Error fetching related articles:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'تاريخ غير معروف';
    
    try {
      const date = new Date(dateString);
      
      // التحقق من أن التاريخ صالح
      if (isNaN(date.getTime())) {
        return 'تاريخ غير معروف';
      }
      
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'تاريخ غير معروف';
    }
  };

  const handleShare = () => {
    if (!article) return;
    
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.description ? article.description.substring(0, 100) + '...' : '',
        url: window.location.href,
      })
      .then(() => console.log('تمت المشاركة بنجاح'))
      .catch((error) => console.log('خطأ في المشاركة', error));
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('تم نسخ الرابط إلى الحافظة'))
        .catch(err => console.error('فشل في نسخ الرابط:', err));
    }
  };

  if (loading) {
    return (
      <div className="article-details-loading" dir="rtl">
        <div className="loading-spinner"></div>
        <p className="loading-text">جاري تحميل المقال...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-details-error" dir="rtl">
        <div className="error-container">
          <h2 className="error-title">حدث خطأ</h2>
          <p className="error-message">{error || 'المقال غير موجود'}</p>
          <button
            onClick={() => navigate('/articles')}
            className="back-button"
          >
            العودة للمقالات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="article-details-page" dir="rtl">
      <div className="article-details-container">
        {/* زر العودة */}
        <div className="back-button-container">
          <button
            onClick={() => navigate('/articles')}
            className="back-button"
          >
            <ArrowRight className="back-icon" />
            <span>العودة للمقالات</span>
          </button>
        </div>

        {/* المقال الرئيسي */}
        <article className="article-main">
          {/* صورة المقال */}
          <div className="article-image-section">
            <img
              src={article.image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200'}
              alt={article.title}
              className="article-image"
              onError={(e) => {
                console.error('فشل تحميل صورة المقال:', article.image);
                e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200';
                
                // محاولة مسار بديل
                if (article.image) {
                  const altPath = article.image.replace(
                    'https://darkslateblue-eel-151368.hostingersite.com/storage/',
                    'https://darkslateblue-eel-151368.hostingersite.com/public/storage/'
                  );
                  setTimeout(() => {
                    const tempImg = new Image();
                    tempImg.onload = () => {
                      e.target.src = altPath;
                    };
                    tempImg.src = altPath;
                  }, 100);
                }
              }}
              onLoad={() => console.log('تم تحميل صورة المقال بنجاح:', article.image)}
            />
            {article.category && (
              <div className="article-category-badge">
                <Tag className="badge-icon" />
                <span className="badge-text">{article.category}</span>
              </div>
            )}
          </div>

          {/* محتوى المقال */}
          <div className="article-content-section">
            <h1 className="article-title">{article.title}</h1>

            {/* معلومات المقال */}
            <div className="article-meta-info">
              <div className="meta-item">
                <Calendar className="meta-icon" />
                <span className="meta-text">{formatDate(article.created_at || article.updated_at)}</span>
              </div>
              
              {article.read_time && (
                <div className="meta-item">
                  <Clock className="meta-icon" />
                  <span className="meta-text">{article.read_time}</span>
                </div>
              )}
              
              <div className="meta-item">
                <Eye className="meta-icon" />
                <span className="meta-text">{article.views || 0} مشاهدة</span>
              </div>
            </div>

            {/* وصف المقال */}
            <div className="article-description">
              <p className="description-text">{article.description}</p>
            </div>

            {/* أزرار الإجراءات */}
            <div className="article-actions">
              <button onClick={handleShare} className="action-button share-button">
                <Share2 className="action-icon" />
                <span>مشاركة</span>
              </button>
              <button
                onClick={() => setBookmarked(!bookmarked)}
                className={`action-button bookmark-button ${bookmarked ? 'active' : ''}`}
              >
                <Bookmark className="action-icon" fill={bookmarked ? 'currentColor' : 'none'} />
                <span>{bookmarked ? 'تم الحفظ' : 'حفظ'}</span>
              </button>
            </div>
          </div>
        </article>

        {/* المقالات ذات الصلة */}
        {relatedArticles.length > 0 && (
          <div className="related-articles-section">
            <h2 className="section-title">مقالات ذات صلة</h2>
            <div className="related-articles-grid">
              {relatedArticles.map(relatedArticle => (
                <div
                  key={relatedArticle.id}
                  className="related-article-card"
                  onClick={() => navigate(`/articles/${relatedArticle.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="related-article-image">
                    <img
                      src={relatedArticle.image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500'}
                      alt={relatedArticle.title}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500';
                      }}
                    />
                  </div>
                  <div className="related-article-content">
                    <h3 className="related-article-title">{relatedArticle.title}</h3>
                    <p className="related-article-description">
                      {relatedArticle.description 
                        ? (relatedArticle.description.length > 100 
                            ? relatedArticle.description.substring(0, 100) + '...'
                            : relatedArticle.description)
                        : 'لا يوجد وصف متاح'}
                    </p>
                    <div className="related-article-meta">
                      {relatedArticle.category && (
                        <span className="related-article-category">{relatedArticle.category}</span>
                      )}
                      {relatedArticle.read_time && (
                        <span className="related-article-time">{relatedArticle.read_time}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* اقتراحات إضافية */}
        <div className="additional-suggestions">
          <h3 className="suggestions-title">مقالات قد تعجبك</h3>
          <button
            onClick={() => navigate('/articles')}
            className="browse-more-button"
          >
            تصفح جميع المقالات
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetails;